import { useState } from 'react'
import cx from 'classnames'
import { useField } from 'formik'
import { useBrandKits } from 'hooks/queries/brandKits/useBrandKits'
import Styles from './Fields.module.scss'
import { Chip } from 'components/Chip/Chip'
import { BrandKitLogo } from 'screens/BrandKit/BrandKitLogo/BrandKitLogo'
import { ChevronRight } from 'blaze-ui/icons/24'
import { Tone } from 'components/VoiceAndTonesSelect/Types'
import { Spinner } from 'common/Spinner/Spinner'
import { Plus } from 'blaze-ui/icons'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { BrandKitCreationDrawer } from 'components/BrandKitCreationDrawer/BrandKitCreationDrawer'
import { Field as BaseField } from 'components/Wizards/MobileWizards/Field/Field'

const BRAND_KITS_LIMIT = 6

interface Props {
  onBrandKitChange?: (brandKitId: number) => void
}

export const BrandKitField = observer(({ onBrandKitChange }: Props) => {
  const { drawersStore } = useRootStore()
  const [brandKitField, , { setValue: setBrandKit }] = useField('brand_kit_id')
  const [toneField, , { setValue: setTone }] = useField<Tone[] | undefined>('tone')
  const [showMore, setShowMore] = useState(false)
  const tones = toneField.value || []
  const [showTones, setShowTones] = useState(tones.length > 0)

  const { data: brandKits, isLoading } = useBrandKits()

  const brandKitsToShow = showMore ? brandKits : brandKits?.slice(0, BRAND_KITS_LIMIT)
  const hasMore = !!brandKits && !!brandKitsToShow && brandKits.length > brandKitsToShow.length
  const shouldShowTones = showTones || (!isLoading && brandKits?.length === 0)

  const onSelectBrandKit = (brandKitId: number) => {
    if (brandKitField.value === brandKitId) {
      setBrandKit('')
    } else {
      setShowTones(false)
      if (!!tones.length) {
        setTone([])
      }
      setBrandKit(brandKitId)
      onBrandKitChange?.(brandKitId)
    }
  }

  const onSelectTone = (tone: Tone) => {
    if (brandKitField.value) {
      setBrandKit('')
    }
    const tonesSet = new Set(toneField.value)
    tonesSet.has(tone) ? tonesSet.delete(tone) : tonesSet.add(tone)
    setTone(Array.from(tonesSet).slice(0, 3))
  }

  const createNewBrandKit = () => {
    drawersStore.openDrawer(BrandKitCreationDrawer, {
      onSuccess: (brandKitId) => {
        setBrandKit(brandKitId)
      },
    })
  }

  return (
    <div className={Styles.brandKitField}>
      <div className={Styles.chips}>
        {isLoading && (
          <Chip deletable={false} variant="catalogue" size="md" className={Styles.chip}>
            <Spinner />
          </Chip>
        )}
        {!isLoading && brandKits && brandKits.length === 0 && (
          <Chip
            deletable={false}
            variant="flat"
            size="md"
            className={cx(Styles.chip, Styles.createNew)}
            onPress={createNewBrandKit}
          >
            <Plus />
            Create Brand Kit
          </Chip>
        )}
        {!!brandKitsToShow?.length &&
          brandKitsToShow.map((kit) => (
            <Chip
              deletable={false}
              variant="flat"
              key={kit.id}
              onPress={() => onSelectBrandKit(kit.id)}
              isSelected={brandKitField.value === kit.id}
              size="md"
              className={Styles.chip}
            >
              <BrandKitLogo brandKit={kit} size={20} textVariant="label" />
              {kit.name}
            </Chip>
          ))}
        {hasMore && (
          <Chip
            deletable={false}
            variant="flat"
            onPress={() => setShowMore(true)}
            isSelected={false}
            size="md"
            className={Styles.chip}
          >
            More...
          </Chip>
        )}
        {!shouldShowTones && (
          <Chip
            deletable={false}
            variant="flat"
            onPress={() => setShowTones(true)}
            isSelected={false}
            size="md"
            className={Styles.chip}
          >
            Pick tones instead
            <ChevronRight size={16} />
          </Chip>
        )}
      </div>
      {shouldShowTones && (
        <BaseField label="Or pick up to 3 tones of voice">
          <div className={Styles.chips}>
            {Object.values(Tone).map((tone) => {
              const isSelected = tones.includes(tone)
              return (
                <Chip
                  deletable={false}
                  variant="flat"
                  key={tone}
                  onPress={() => onSelectTone(tone)}
                  isSelected={isSelected}
                  isDisabled={tones.length >= 3 && !isSelected}
                  size="md"
                  className={Styles.chip}
                >
                  {tone}
                </Chip>
              )
            })}
          </div>
        </BaseField>
      )}
    </div>
  )
})
